import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {FullscreenLayer} from '@volkswagen-onehub/components-core';
import GradientMeshBackground from '../components/GradientMeshBackground';
import Div from '../components/Div.styled';
import CloseButtonSVG from '../components/CloseButtonSVG';
import {CSSTransition} from 'react-transition-group';
import './chat.css';
import useMediaQuery from '../hooks/useMediaQuery';
import {MessageBubble, MessageText, PromptOption, StyledDiv} from './styles';
import MessageHandler from './MessageHandler';
import {Message} from './types';
import LottieButtonChatCTA from '../components/LottieButtonChatCTA';
import MessageElement from './MessageElement';
import isEmpty from 'lodash/isEmpty';
import {useSendChatAnalytic} from '../hooks/useSendChatAnalytic';
import {Variants, motion} from 'framer-motion';

const container: Variants = {
  hidden: {opacity: 0},
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.75,
    },
  },
};

const listItem: Variants = {
  hidden: {opacity: 0},
  show: {
    opacity: 1,
  },
};

const MemoizedMessageElement = React.memo(MessageElement);

interface ChatWindowProps {
  messages: Message[];
  active: boolean;
  onClose: () => void;
  handleSendMessage: (message: string) => void;
  isFromCTA?: boolean;
  loading: boolean;
  promptSuggestions?: string[];
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  active,
  onClose,
  handleSendMessage,
  loading,
  promptSuggestions,
}) => {
  const [isPromptVisible, setIsPromptVisible] = useState<boolean>(true);
  const {isMobile} = useMediaQuery();
  const {handleOnExitClick} = useSendChatAnalytic();
  const [closeButtonOffset, setCloseButtonOffset] = useState<number>(0);

  const promptOptions = useMemo(
    () =>
      promptSuggestions?.map((prompt) => ({label: prompt, value: prompt})) ||
      [],
    [promptSuggestions],
  );
  const hasPromptSuggestions = !isEmpty(promptSuggestions);

  const onCloseWindow = () => {
    onClose();
    handleOnExitClick();
  };

  const calculateCloseButtonOffset = useCallback(() => {
    const headerHeight =
      document.querySelector('header[role="banner"], div[role="banner"]')
        ?.clientHeight || 56;
    const closeOffset = headerHeight + (isMobile ? 16 : 24);
    setCloseButtonOffset(closeOffset);
  }, [isMobile]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(calculateCloseButtonOffset);

    const header = document.querySelector(
      'header[role="banner"], div[role="banner"]',
    );
    if (header) {
      calculateCloseButtonOffset();
      resizeObserver.observe(header);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateCloseButtonOffset]);

  const messagesList = useMemo(
    () =>
      messages
        .slice(hasPromptSuggestions ? 2 : 0)
        .reverse()
        .map((message, i) => (
          <MemoizedMessageElement
            key={`${message.text}-${i}`}
            message={message}
          />
        )),
    [messages, hasPromptSuggestions],
  );

  return (
    <CSSTransition
      in={active}
      classNames="alert"
      timeout={{enter: 500, exit: 300}}
      unmountOnExit
    >
      <StyledDiv id="chat-container">
        <FullscreenLayer active={active}>
          <Div
            customStyles={{
              height: '100%',
              fontSize: isMobile ? 14 : 16,
            }}
            padding={isMobile ? '72px 16px 16px 16px' : '80px 48px 48px 48px'}
          >
            <GradientMeshBackground />
            <Div
              customStyles={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CloseButtonSVG
                onClick={onCloseWindow}
                opacity={1}
                style={{
                  position: 'absolute',
                  top: closeButtonOffset,
                  right: isMobile ? 16 : 60,
                }}
              />
              <Div
                customStyles={{
                  height: '100%',
                  flex: 10,
                  maxWidth: isMobile ? 375 : 600,
                  width: '100%',
                  overflow: 'auto',
                  padding: '0 16px 40px 16px',
                  display: 'flex',
                  flexFlow: 'column',
                  gap: 8,
                  flexDirection: 'column-reverse',
                }}
              >
                <Div
                  customStyles={{
                    marginTop: isMobile ? 8 : 16,
                    padding: '0 16px',
                    flexFlow: 'column',
                    alignItems: 'flex-start',
                    maxWidth: isMobile ? 375 : 600,
                    width: '100%',
                    display: loading ? 'flex' : 'none',
                  }}
                >
                  <LottieButtonChatCTA />
                </Div>
                {messagesList}
                {hasPromptSuggestions && (
                  <Div
                    customStyles={{
                      position: 'relative',
                      alignSelf: 'flex-start',
                      flexFlow: 'column',
                      gap: 16,
                      display: 'flex',
                    }}
                  >
                    <MessageBubble
                      key={'chat-cta'}
                      isUser={false}
                      isMobile={isMobile}
                    >
                      Let’s get started. Please type your question or pick a
                      popular prompt below.
                    </MessageBubble>
                    <React.Fragment>
                      {isPromptVisible && promptOptions.length > 0 && (
                        <motion.div
                          variants={container}
                          initial="hidden"
                          animate="show"
                        >
                          {promptOptions.map((option, i) => (
                            <motion.div
                              style={{marginBlock: 16}}
                              key={i}
                              variants={listItem}
                            >
                              <PromptOption
                                onClick={() => {
                                  setIsPromptVisible(false);
                                  handleSendMessage(option.label);
                                }}
                              >
                                <MessageText lineHeight={18}>
                                  {option.label}
                                </MessageText>
                              </PromptOption>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </React.Fragment>
                  </Div>
                )}
              </Div>
              <Div
                customStyles={{
                  padding: '0 16px',
                  maxWidth: isMobile ? 375 : 600,
                  width: '100%',
                }}
              >
                <MessageHandler onSendMessage={handleSendMessage} />
              </Div>
            </Div>
          </Div>
        </FullscreenLayer>
      </StyledDiv>
    </CSSTransition>
  );
};

export default memo(ChatWindow);
